import {Separator, Slider} from 'platform/components';
import {Grid, GridItem, Heading, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isNil} from 'ramda';

import {CompositionFont} from '@dms/api/sales';
import {
  FinancingCompositionLayer,
  FinancingCompositionScene,
  FinancialProductType,
} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {LayerBox} from '../types/CompositionEditorAction';
import {applyLayerBoxInput} from '../utils/applyLayerBoxInput';
import {DraftIntegerField} from './DraftIntegerField';
import {ImageLayerFields} from './ImageLayerFields';
import {PhotoLayerFields} from './PhotoLayerFields';
import {RectLayerFields} from './RectLayerFields';
import {TextLayerFields} from './TextLayerFields';

interface LayerInspectorProps extends TestIdProps {
  layer: FinancingCompositionLayer | null;
  canvas: FinancingCompositionScene['canvas'];
  fonts: CompositionFont[];
  isLoadingFonts: boolean;
  // Set in financing mode only; enables the data-field token picker for text layers.
  financialProductType?: FinancialProductType;
  onChange: (layerId: string, patch: Partial<FinancingCompositionLayer>) => void;
  onFontDeleted: (fontId: string) => void;
  onResize: (layerId: string, box: LayerBox) => void;
  onImageUploaded: (fileId: string, url: string) => void;
}

const OPACITY_PERCENT = 100;

export function LayerInspector(props: LayerInspectorProps) {
  const layer = props.layer;

  if (isNil(layer)) {
    return (
      <Text size="small" color="secondary" data-testid={suffixTestId('empty', props)}>
        {i18n.t('entity.watermark.composition.labels.noLayerSelected')}
      </Text>
    );
  }

  const commitBoxValue = (field: keyof LayerBox) => (value: number) =>
    props.onResize(layer.id, applyLayerBoxInput(layer, field, value, props.canvas));

  const typeFields = match(layer)
    .with({type: 'text'}, (textLayer) => (
      <TextLayerFields
        layer={textLayer}
        fonts={props.fonts}
        isLoadingFonts={props.isLoadingFonts}
        financialProductType={props.financialProductType}
        onChange={(patch) => props.onChange(layer.id, patch)}
        onFontDeleted={props.onFontDeleted}
        data-testid={suffixTestId('text', props)}
      />
    ))
    .with({type: 'image'}, (imageLayer) => (
      <ImageLayerFields
        layer={imageLayer}
        onChange={(patch) => props.onChange(layer.id, patch)}
        onImageUploaded={props.onImageUploaded}
        data-testid={suffixTestId('image', props)}
      />
    ))
    .with({type: 'rect'}, (rectLayer) => (
      <RectLayerFields
        layer={rectLayer}
        onChange={(patch) => props.onChange(layer.id, patch)}
        data-testid={suffixTestId('rect', props)}
      />
    ))
    .with({type: 'photo'}, (photoLayer) => (
      <PhotoLayerFields
        layer={photoLayer}
        onChange={(patch) => props.onChange(layer.id, patch)}
        data-testid={suffixTestId('photo', props)}
      />
    ))
    .exhaustive();

  return (
    <VStack spacing={3}>
      <Heading size={5}>{i18n.t('entity.watermark.composition.labels.layerProperties')}</Heading>
      {/* Keyed by layer so a draft typed for one layer never carries over to another. */}
      <Grid key={layer.id} columns={2}>
        <GridItem>
          <DraftIntegerField
            label="X"
            value={layer.x}
            onCommit={commitBoxValue('x')}
            data-testid={suffixTestId('x', props)}
          />
        </GridItem>
        <GridItem>
          <DraftIntegerField
            label="Y"
            value={layer.y}
            onCommit={commitBoxValue('y')}
            data-testid={suffixTestId('y', props)}
          />
        </GridItem>
        <GridItem>
          <DraftIntegerField
            label={i18n.t('general.labels.width')}
            value={layer.width}
            onCommit={commitBoxValue('width')}
            data-testid={suffixTestId('width', props)}
          />
        </GridItem>
        <GridItem>
          <DraftIntegerField
            label={i18n.t('general.labels.height')}
            value={layer.height}
            onCommit={commitBoxValue('height')}
            data-testid={suffixTestId('height', props)}
          />
        </GridItem>
      </Grid>
      <Slider
        label={i18n.t('entity.watermark.composition.labels.opacity')}
        value={Math.round(layer.opacity * OPACITY_PERCENT)}
        min={0}
        max={OPACITY_PERCENT}
        step={1}
        ticks={2}
        onChange={(value) => props.onChange(layer.id, {opacity: value / OPACITY_PERCENT})}
        data-testid={suffixTestId('opacity', props)}
      />
      <Separator spacing={0} />
      {typeFields}
    </VStack>
  );
}
